import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { bussolaData, BussolaCategory, BussolaSubcategory } from './data';
import './bussola.css';

interface BussolaProps {
  isOpen: boolean;
  onClose: () => void;
}

function Bussola({ isOpen, onClose }: BussolaProps) {
  const [selectedCategory, setSelectedCategory] = useState<BussolaCategory | null>(null);
  const [selectedSub, setSelectedSub] = useState<BussolaSubcategory | null>(null);

  function handleClose() {
    setSelectedCategory(null);
    setSelectedSub(null);
    onClose();
  }

  function handleBack() {
    if (selectedSub) {
      setSelectedSub(null);
    } else if (selectedCategory) {
      setSelectedCategory(null);
    } else {
      handleClose();
    }
  }

  function handleCategoryClick(cat: BussolaCategory) {
    setSelectedCategory(cat);
    setSelectedSub(null);
  }

  function handleSubClick(sub: BussolaSubcategory) {
    setSelectedSub(sub);
  }

  const breadcrumb = selectedSub
    ? `${selectedCategory?.label} › ${selectedSub.label}`
    : selectedCategory
    ? selectedCategory.label
    : null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="bussola-widget"
          initial={{ opacity: 0, y: 12, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, scale: 0.95 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
        >
          {/* Header */}
          <div className="bussola-header">
            {breadcrumb ? (
              <button className="bussola-back" onClick={handleBack}>
                <i className="fa-solid fa-chevron-left" />
              </button>
            ) : (
              <div className="bussola-header-icon">
                <i className="fa-solid fa-compass" />
              </div>
            )}
            <div className="bussola-header-text">
              <span className="bussola-title">
                {breadcrumb || 'Bússola'}
              </span>
              <span className="bussola-subtitle">
                {selectedSub
                  ? selectedSub.labelPt
                  : selectedCategory
                  ? selectedCategory.labelPt
                  : 'O que você quer dizer?'}
              </span>
            </div>
            <button className="bussola-close" onClick={handleClose}>
              <i className="fa-solid fa-xmark" />
            </button>
          </div>

          {/* Content */}
          <div className="bussola-content">
            <AnimatePresence mode="wait">
              {/* Level 1: Category grid */}
              {!selectedCategory && (
                <motion.div
                  key="categories"
                  className="bussola-grid"
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  transition={{ duration: 0.12 }}
                >
                  {bussolaData.map((cat) => (
                    <button
                      key={cat.id}
                      className="bussola-cat-btn"
                      onClick={() => handleCategoryClick(cat)}
                    >
                      <div
                        className="bussola-cat-icon"
                        style={{ background: `${cat.color}20`, borderColor: `${cat.color}40` }}
                      >
                        <i className={cat.icon} style={{ color: cat.color }} />
                      </div>
                      <span className="bussola-cat-label">{cat.label}</span>
                    </button>
                  ))}
                </motion.div>
              )}

              {/* Level 2: Subcategory list */}
              {selectedCategory && !selectedSub && (
                <motion.div
                  key={`subs-${selectedCategory.id}`}
                  className="bussola-subs"
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 16 }}
                  transition={{ duration: 0.12 }}
                >
                  {selectedCategory.subcategories.map((sub) => (
                    <button
                      key={sub.id}
                      className="bussola-sub-btn"
                      onClick={() => handleSubClick(sub)}
                    >
                      <span className="bussola-sub-label">{sub.label}</span>
                      <i className="fa-solid fa-chevron-right bussola-sub-arrow" />
                    </button>
                  ))}
                </motion.div>
              )}

              {/* Level 3: Patterns or Words */}
              {selectedSub && (
                <motion.div
                  key={`detail-${selectedSub.id}`}
                  className="bussola-detail"
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 16 }}
                  transition={{ duration: 0.12 }}
                >
                  {selectedSub.patterns && selectedSub.patterns.map((p, i) => (
                    <div key={i} className="bussola-pattern-card">
                      <span
                        className="bussola-pattern-formula"
                        style={{ color: selectedCategory?.color }}
                      >
                        {p.pattern}
                      </span>
                      <p className="bussola-pattern-example">"{p.example}"</p>
                      <p className="bussola-pattern-example-pt">{p.examplePt}</p>
                    </div>
                  ))}

                  {selectedSub.words && (
                    <div className="bussola-words-grid">
                      {selectedSub.words.map((w, i) => (
                        <div key={i} className="bussola-word-chip">
                          <span className="bussola-word-en">{w.en}</span>
                          <span className="bussola-word-pt">{w.pt}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default Bussola;
